import { Button, Form, Input, message, Modal, Rate, Switch, Upload } from 'antd';
import { UploadChangeParam, UploadFile } from 'antd/es/upload';

import { EyeOutlined, PlusOutlined } from '@ant-design/icons';
import SVGIcon from 'components/SVGIcon/SVGIcon';
import { getImageByCategory } from 'containers/MarketPage/ImageByCategory';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import translations from 'translations';
import { FeedbackProductInput, Product, ResourceGroup } from 'types/schema';
import media from 'utils/mediaStyle';
import utilMessages from 'utils/messages';
import { handleUploadFile } from 'utils/uploadImageToServer';
import RemoveIcon from '../_icons/removeIcon.svg';
import VideoPlayIcon from '../_icons/VideoPlayIcon.svg';

const { TextArea } = Input;

const MAX_IMAGE_SIZE_MB = 2;
const MAX_VIDEO_SIZE_MB = 20;

const AModal = styled(Modal)`
  width: 95% !important;
  max-width: 560px !important;
  .ant-modal-title {
    text-align: center !important;
    font-size: 18px !important;
    line-height: 24px !important;
    font-weight: 500 !important;
    color: #242527 !important;

    ${media.md`
      font-size: 20px !important;
      line-height: 26px !important;
    `};
  }
  .ant-modal-close {
    top: 16px !important;
  }
  .ant-modal-close-x {
    font-size: 18px !important;
  }

  .ant-modal-content {
    padding: 16px !important;
    border-radius: 16px !important;
  }

  ${media.md`
    .ant-modal-content{
      padding: 20px 24px !important;
    }
  `}
`;

const Container = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  align-content: self-start;

  .ant-form {
    width: 100% !important;
  }
  .ant-form-item {
    margin-bottom: 0px !important;
  }

  .ant-form-item-label > label {
    font-size: 14px !important;
    font-weight: 500 !important;
    line-height: 20px !important;
    color: #596056 !important;
    margin-bottom: 4px !important;
  }
  .ant-input-lg {
    font-size: 14px !important;
  }
  .ant-input {
    line-height: 20px !important;
    color: #2f382c !important;
  }
  .ant-upload-wrapper.ant-upload-picture-card-wrapper .ant-upload.ant-upload-select {
    width: 52px !important;
    height: 52px !important;
    ${media.md`
      width: 80px !important;
      height: 80px   !important;
    `}

    background: #ffffff !important;
    border-radius: 6px !important;
    border-color: #43a047 !important;
  }
  .ant-upload-list-item-container {
    width: 52px !important;
    height: 52px !important;
    ${media.md`
      width: 80px !important;
      height: 80px   !important;
    `}
  }
  .ant-upload-list.ant-upload-list-picture-card {
    gap: 16px !important;
  }
`;

const ProductImage = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 8px;
  object-fit: cover;

  ${media.md`
    width: 100px;
    height: 100px;
  `}
`;

const ProductName = styled.div`
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: #2f382c;
  margin-top: 8px;
  text-align: center;

  ${media.md`
    font-size: 16px;
    line-height: 20px;
  `}
`;

const Stars = styled(Rate)`
  width: 100% !important;
  margin-top: 24px;
  font-size: 30px !important;
  display: flex;
  justify-content: center;
`;

const StarContent = styled.div`
  margin: 0 auto;
  margin-top: 8px;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  color: #2f382c;
  text-align: center;
`;

const ReviewTextArea = styled(TextArea)`
  resize: none !important;
`;

const OverlayImageContainer = styled.div`
  display: none;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  cursor: pointer;
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 6px;
  border: 1px solid #eaeaeb;

  &:hover ${OverlayImageContainer} {
    display: flex;
  }

  foreignObject {
    display: none !important;
  }
  defs {
    display: none !important;
  }
`;
const ImageItem = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 6px;
  object-fit: cover;
  cursor: pointer;
`;
const VideoFrame = styled.video`
  width: 100%;
  height: 100%;
  border-radius: 6px;
  object-fit: cover;
  cursor: pointer;
`;

const RemoveIconContainer = styled.div`
  position: absolute;
  top: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #ffffff;
  box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.12);
  border-radius: 50%;
  z-index: 10;
  cursor: pointer;
`;

const AnonymousCommentContainer = styled.div`
  margin-top: 16px;
  padding-top: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: row-reverse;
  gap: 12px;
  border-top: 1px solid #eaebea;

  ${media.md`
    margin-top: 24px;
    padding-top: 0px;
    justify-content: flex-start;
    flex-direction: row;
    border-top: none;
  `}
`;
const AnonymousCommentLabel = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  color: #596056;
`;

const ConfirmButtonContainer = styled.div`
  width: 100%;
  margin-top: 16px !important;
  display: flex;
  justify-content: center;

  ${media.md`
    margin-top: 40px !important;
  `}
`;
const ConfirmButton = styled(Button)`
  width: 100%;
  height: 40px;
  font-weight: 500 !important;
  font-size: 14px !important;
  background: #43a047;
  box-shadow: none !important;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border: none !important;

  ${media.md`
    max-width: 200px;
    font-size: 16px !important;
  `}
`;

const PlayIconContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const CustomStar = ({
  fillColor = '#F5CE47',
  strokeColor = '#E3B205',
}: {
  fillColor?: string;
  strokeColor?: string;
}) => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
      <mask id="path-1-inside-1_32907_44022" fill="white">
        <path d="M14.8048 0.695206C15.1011 0.0782986 15.8208 -0.175724 16.4132 0.1288C16.6494 0.24987 16.8409 0.449179 16.9571 0.695206L21.213 9.68349L30.7345 11.1249C31.3906 11.2264 31.8483 11.8594 31.7472 12.5429C31.7097 12.8162 31.5859 13.0663 31.3985 13.2577L24.5099 20.2538L26.1368 30.1327C26.2493 30.8124 25.8107 31.4569 25.1583 31.5741C24.8996 31.621 24.6291 31.578 24.3966 31.4491L15.881 26.785L7.36436 31.4491C7.16637 31.558 6.94315 31.6073 6.71983 31.5907C6.49652 31.5741 6.28183 31.4924 6.10069 31.3554C5.91974 31.2184 5.77961 31.0312 5.69541 30.8153C5.61119 30.5992 5.58596 30.3625 5.62413 30.1327L7.25206 20.2538L0.363384 13.2577C0.135304 13.0261 0.00432101 12.7096 0.000102382 12.3778C-0.00405968 12.0461 0.118828 11.726 0.340923 11.4882C0.524646 11.293 0.765046 11.1639 1.02745 11.1249L10.548 9.68349L14.8048 0.695206Z" />
      </mask>
      <path
        d="M14.8048 0.695206C15.1011 0.0782986 15.8208 -0.175724 16.4132 0.1288C16.6494 0.24987 16.8409 0.449179 16.9571 0.695206L21.213 9.68349L30.7345 11.1249C31.3906 11.2264 31.8483 11.8594 31.7472 12.5429C31.7097 12.8162 31.5859 13.0663 31.3985 13.2577L24.5099 20.2538L26.1368 30.1327C26.2493 30.8124 25.8107 31.4569 25.1583 31.5741C24.8996 31.621 24.6291 31.578 24.3966 31.4491L15.881 26.785L7.36436 31.4491C7.16637 31.558 6.94315 31.6073 6.71983 31.5907C6.49652 31.5741 6.28183 31.4924 6.10069 31.3554C5.91974 31.2184 5.77961 31.0312 5.69541 30.8153C5.61119 30.5992 5.58596 30.3625 5.62413 30.1327L7.25206 20.2538L0.363384 13.2577C0.135304 13.0261 0.00432101 12.7096 0.000102382 12.3778C-0.00405968 12.0461 0.118828 11.726 0.340923 11.4882C0.524646 11.293 0.765046 11.1639 1.02745 11.1249L10.548 9.68349L14.8048 0.695206Z"
        fill={fillColor}
      />
      <path
        d="M14.8048 0.695206L13.9034 0.262223L13.901 0.267183L14.8048 0.695206ZM16.4132 0.1288L15.956 1.01816L15.9571 1.01871L16.4132 0.1288ZM16.9571 0.695206L16.053 1.12244L16.0533 1.12315L16.9571 0.695206ZM21.213 9.68349L20.3092 10.1114L20.537 10.5925L21.0633 10.6722L21.213 9.68349ZM30.7345 11.1249L30.8874 10.1367L30.8842 10.1362L30.7345 11.1249ZM31.7472 12.5429L30.7579 12.3965L30.7564 12.407L31.7472 12.5429ZM31.3985 13.2577L32.1111 13.9593L32.1131 13.9573L31.3985 13.2577ZM24.5099 20.2538L23.7973 19.5522L23.4405 19.9145L23.5232 20.4163L24.5099 20.2538ZM26.1368 30.1327L25.1501 30.2952L25.1502 30.296L26.1368 30.1327ZM25.1583 31.5741L24.9814 30.5899L24.98 30.5901L25.1583 31.5741ZM24.3966 31.4491L24.8815 30.5745L24.877 30.572L24.3966 31.4491ZM15.881 26.785L16.3613 25.908L15.881 25.6449L15.4006 25.908L15.881 26.785ZM7.36436 31.4491L6.88403 30.572L6.88245 30.5729L7.36436 31.4491ZM6.71983 31.5907L6.64574 32.588L6.64576 32.588L6.71983 31.5907ZM6.10069 31.3554L5.49712 32.1527L5.49735 32.1528L6.10069 31.3554ZM5.69541 30.8153L4.76369 31.1785L4.76377 31.1787L5.69541 30.8153ZM5.62413 30.1327L6.61061 30.2965L6.61082 30.2953L5.62413 30.1327ZM7.25206 20.2538L8.23875 20.4164L8.32144 19.9146L7.96461 19.5522L7.25206 20.2538ZM0.363384 13.2577L1.07594 12.5561L1.07592 12.5561L0.363384 13.2577ZM0.000102382 12.3778L-0.999819 12.3904L-0.999817 12.3905L0.000102382 12.3778ZM0.340923 11.4882L-0.387252 10.8028L-0.389885 10.8056L0.340923 11.4882ZM1.02745 11.1249L1.17464 12.114L1.17714 12.1136L1.02745 11.1249ZM10.548 9.68349L10.6976 10.6722L11.2239 10.5925L11.4517 10.1115L10.548 9.68349ZM14.8048 0.695206L15.7062 1.12818C15.7387 1.0605 15.7869 1.02493 15.8303 1.00962C15.8727 0.994676 15.9144 0.99677 15.956 1.01816L16.4132 0.1288L16.8704 -0.760564C15.7525 -1.33522 14.4311 -0.83649 13.9034 0.26223L14.8048 0.695206ZM16.4132 0.1288L15.9571 1.01871C15.9919 1.03658 16.0285 1.07062 16.053 1.12244L16.9571 0.695206L17.8613 0.267967C17.6532 -0.172259 17.3069 -0.536843 16.8693 -0.76111L16.4132 0.1288ZM16.9571 0.695206L16.0533 1.12315L20.3092 10.1114L21.213 9.68349L22.1168 9.25554L17.8609 0.267263L16.9571 0.695206ZM21.213 9.68349L21.0633 10.6722L30.5848 12.1136L30.7345 11.1249L30.8842 10.1362L21.3627 8.69475L21.213 9.68349ZM30.7345 11.1249L30.5815 12.1131C30.6701 12.1268 30.7823 12.2321 30.7579 12.3966L31.7472 12.5429L32.7364 12.6892C32.9143 11.4867 32.1112 10.326 30.8874 10.1367L30.7345 11.1249ZM31.7472 12.5429L30.7564 12.407C30.7481 12.468 30.7209 12.5204 30.684 12.5582L31.3985 13.2577L32.1131 13.9573C32.4509 13.6122 32.6713 13.1643 32.7379 12.6787L31.7472 12.5429ZM31.3985 13.2577L30.686 12.5561L23.7973 19.5522L24.5099 20.2538L25.2224 20.9554L32.1111 13.9593L31.3985 13.2577ZM24.5099 20.2538L23.5232 20.4163L25.1501 30.2952L26.1368 30.1327L27.1235 29.9702L25.4966 20.0913L24.5099 20.2538ZM26.1368 30.1327L25.1502 30.296C25.1789 30.4692 25.0632 30.5752 24.9814 30.5899L25.1583 31.5741L25.3352 32.5583C26.5583 32.3386 27.3197 31.1556 27.1234 29.9694L26.1368 30.1327ZM25.1583 31.5741L24.98 30.5901C24.9453 30.5964 24.9095 30.5901 24.8815 30.5745L24.3966 31.4491L23.9117 32.3237C24.3487 32.566 24.8538 32.6455 25.3366 32.5581L25.1583 31.5741ZM24.3966 31.4491L24.877 30.572L16.3613 25.908L15.881 26.785L15.4006 27.6621L23.9162 32.3262L24.3966 31.4491ZM15.881 26.785L15.4006 25.908L6.88403 30.572L7.36436 31.4491L7.84469 32.3262L16.3613 27.6621L15.881 26.785ZM7.36436 31.4491L6.88245 30.5729C6.85147 30.5899 6.82105 30.5955 6.7939 30.5935L6.71983 31.5907L6.64576 32.588C7.06525 32.6191 7.48127 32.5261 7.84627 32.3253L7.36436 31.4491ZM6.71983 31.5907L6.79392 30.5935C6.76595 30.5914 6.73439 30.5808 6.70403 30.5579L6.10069 31.3554L5.49735 32.1528C5.82928 32.404 6.22708 32.5569 6.64574 32.588L6.71983 31.5907ZM6.10069 31.3554L6.70425 30.558C6.67491 30.5358 6.64606 30.5007 6.62706 30.452L5.69541 30.8153L4.76377 31.1787C4.91316 31.5617 5.16457 31.9009 5.49712 32.1527L6.10069 31.3554ZM5.69541 30.8153L6.62714 30.4522C6.60769 30.4022 6.60215 30.3475 6.61061 30.2965L5.62413 30.1327L4.63764 29.9689C4.56977 30.3775 4.6147 30.7962 4.76369 31.1785L5.69541 30.8153ZM5.62413 30.1327L6.61082 30.2953L8.23875 20.4164L7.25206 20.2538L6.26536 20.0912L4.63743 29.9701L5.62413 30.1327ZM7.25206 20.2538L7.96461 19.5522L1.07594 12.5561L0.363384 13.2577L-0.349172 13.9593L6.5395 20.9554L7.25206 20.2538ZM0.363384 13.2577L1.07592 12.5561C1.0324 12.5119 1.00102 12.4437 1.00002 12.3651L0.000102382 12.3778L-0.999817 12.3905C-0.992379 12.9755 -0.761795 13.5403 -0.349149 13.9593L0.363384 13.2577ZM0.000102382 12.3778L1.00002 12.3653C0.999035 12.2865 1.02884 12.2167 1.07173 12.1708L0.340923 11.4882L-0.389885 10.8056C-0.791187 11.2352 -1.00715 11.8057 -0.999819 12.3904L0.000102382 12.3778ZM0.340923 11.4882L1.06909 12.1736C1.10519 12.1352 1.14359 12.1186 1.17463 12.114L1.02745 11.1249L0.880257 10.1358C0.386497 10.2093 -0.0559001 10.4508 -0.387246 10.8028L0.340923 11.4882ZM1.02745 11.1249L1.17714 12.1136L10.6976 10.6722L10.548 9.68349L10.3983 8.69475L0.877752 10.1362L1.02745 11.1249ZM10.548 9.68349L11.4517 10.1115L15.7086 1.12323L14.8048 0.695206L13.901 0.267183L9.64419 9.25546L10.548 9.68349Z"
        fill={strokeColor}
        mask="url(#path-1-inside-1_32907_44022)"
      />
    </svg>
  );
};

export const normFile = (data: UploadChangeParam<UploadFile>) => {
  if (Array.isArray(data)) {
    return data;
  }
  return data && data.fileList;
};

interface AddReviewProductModalProps {
  openReviewModal: boolean;
  setOpenReviewModal: (value: boolean) => void;
  product?: Product;
  onCheckCanReviewProduct: (data: { callback: (canReview: boolean) => void }) => void;
  onSubmitReviewProduct: (data: { callback: (success: boolean) => void; input: FeedbackProductInput }) => void;
}

const starContentMap = {
  1: translations(utilMessages.terrible),
  2: translations(utilMessages.bad),
  3: translations(utilMessages.okay),
  4: translations(utilMessages.good),
  5: translations(utilMessages.great),
};

const AddReviewProductModal = (props: AddReviewProductModalProps) => {
  const { openReviewModal, setOpenReviewModal, product, onCheckCanReviewProduct, onSubmitReviewProduct } = props;
  const [form] = Form.useForm();

  const commentValue = Form.useWatch('comment', form);
  const fileListData = Form.useWatch('image_urls', form);

  // state to control show/hide add image button smoothly
  const [isShowAddImageBtn, setIsShowAddImageBtn] = useState(true);

  // cache file url to prevent re-generate url for item in file list
  const fileUrlCache = useRef(new Map<string, string>());

  const [starValue, setStarValue] = useState(5);

  // prevent spam multiple submit
  const isSubmitting = useRef(false);

  const [isLoading, setIsLoading] = useState(false);
  const [previewFile, setPreviewFile] = useState<{
    type: 'image' | 'video';
    url: string;
  }>();

  const generateImage = (product) => {
    return product?.category && !product?.imageUrl ? getImageByCategory(product?.category) : product?.imageUrl;
  };

  const handleRemoveFile = useCallback(
    (file: UploadFile) => {
      const currentFileList: UploadFile[] = fileListData || [];

      const urlToRemove = fileUrlCache.current.get(file.uid) || file.url;

      if (urlToRemove) {
        URL.revokeObjectURL(urlToRemove);
        fileUrlCache.current.delete(file.uid);
      }

      const newFileList = currentFileList.filter((item) => item.uid !== file.uid);
      form.setFieldsValue({
        image_urls: newFileList,
      });
    },
    [fileListData, form],
  );

  const handleBeforeUpload = useCallback(
    (file, fileList) => {
      const currentFileList = fileListData || [];
      if (currentFileList.length + (fileList?.length || 0) > 5) {
        message.error(translations(utilMessages.maximumUploadFilesLimit));
        return Upload.LIST_IGNORE;
      }

      const isVideo = file.type?.startsWith('video/');

      if (isVideo) {
        if (file.type !== 'video/mp4' && file.type !== 'video/quicktime') {
          message.error(translations(utilMessages.onlyMp4VideoSupported));
          return Upload.LIST_IGNORE;
        }
        const isLt20MB = file.size / 1024 / 1024 < MAX_VIDEO_SIZE_MB;
        if (!isLt20MB) {
          message.error(translations(utilMessages.videoSizeLimit));
          return Upload.LIST_IGNORE;
        }
      } else {
        const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg';
        if (!isJpgOrPng) {
          message.error(translations(utilMessages.onlyJpgOrPngImageSupported));
          return Upload.LIST_IGNORE;
        }
        const isLt2MB = file.size / 1024 / 1024 < MAX_IMAGE_SIZE_MB;
        if (!isLt2MB) {
          message.error(translations(utilMessages.imageSizeLimit));
          return Upload.LIST_IGNORE;
        }
      }

      return false;
    },
    [fileListData],
  );

  const cleanupUrl = useCallback(() => {
    fileUrlCache.current.forEach(URL.revokeObjectURL);
    fileUrlCache.current.clear();
  }, []);

  const onFinish = (values) => {
    if (isSubmitting.current) {
      return;
    }
    setIsLoading(true);
    isSubmitting.current = true;
    onCheckCanReviewProduct({
      callback: async (canReview) => {
        if (!canReview) {
          isSubmitting.current = false;
          setIsLoading(false);
          return;
        } else {
          const { rating, comment, image_urls, isAnonymous } = values;

          let imageItems: any[] = [];
          if (image_urls?.length) {
            const listApiUploads =
              image_urls?.map((file) => handleUploadFile(file.originFileObj, ResourceGroup.ProductImage)) || [];

            const uploadedImageResponses = await Promise.all(listApiUploads);
            imageItems = uploadedImageResponses?.filter((image: any) => !image?.error) || [];

            if (imageItems.length !== image_urls.length) {
              message.error(translations(utilMessages.uploadImageFailed));
              setIsLoading(false);
              isSubmitting.current = false;
              return;
            }
          }

          const reviewData = {
            objectId: product?.id as string,
            rating,
            comment,
            medias:
              imageItems?.map((item) => ({
                url: item.fullUrl,
                name: item.name,
                type: item.type?.includes('image') ? 'IMAGE' : 'VIDEO',
              })) || [],
            isAnonymous: !!isAnonymous,
          };
          onSubmitReviewProduct({
            callback: (success) => {
              setIsLoading(false);
              isSubmitting.current = false;
              if (!success) {
                return;
              }
              setOpenReviewModal(false);
              form.resetFields();
            },
            input: reviewData,
          });
        }
      },
    });
  };

  useEffect(() => {
    if (fileListData && fileListData.length >= 5) {
      setIsShowAddImageBtn(false);
    } else {
      setTimeout(() => {
        setIsShowAddImageBtn(true);
      }, 400);
    }
  }, [fileListData]);
  return (
    <>
      <AModal
        className="add-review-product-modal"
        destroyOnHidden={false}
        title={translations(utilMessages.productReview)}
        closable={true}
        open={openReviewModal}
        footer={null}
        centered
        onCancel={() => {
          setOpenReviewModal(false);
          cleanupUrl();
          form.resetFields();
        }}
      >
        <Container>
          <ProductImage src={generateImage(product)} alt={product?.name} />
          <ProductName>{product?.name}</ProductName>
          <Form onFinish={onFinish} form={form} size="large" layout="vertical">
            <Form.Item name="rating" colon={false} required={false} initialValue={5}>
              <Stars
                character={({ index, value }) =>
                  index < value ? <CustomStar /> : <CustomStar fillColor="#FFF7D6" strokeColor="#F8E6A0" />
                }
                defaultValue={5}
                onHoverChange={(value) => {
                  setStarValue((prev) => {
                    if (!!value && value !== prev) return value;
                    return prev;
                  });
                }}
                onMouseLeave={() => {
                  setStarValue((prev) => {
                    const current = form.getFieldValue('rating');
                    if (!!current && prev !== current) return current;
                    return prev;
                  });
                }}
                allowClear={false}
              />
            </Form.Item>
            <StarContent>{starContentMap[starValue]}</StarContent>
            <Form.Item
              name="comment"
              label={translations(utilMessages.writeAReview)}
              rules={[
                { required: true, message: translations(utilMessages.pleaseEnterYourReview) },
                { min: 10, message: translations(utilMessages.reviewMinLength) },
                { max: 200, message: translations(utilMessages.reviewMaxLength) },
              ]}
              style={{
                marginTop: '24px',
              }}
            >
              <ReviewTextArea
                rows={3}
                placeholder={translations(utilMessages.howDoYouFeel)}
                style={{ resize: 'none' }}
                minLength={10}
                maxLength={200}
              />
            </Form.Item>
            <Form.Item
              name="image_urls"
              label={translations(utilMessages.addImageVideo)}
              colon={false}
              required={false}
              style={{
                marginTop: '16px',
              }}
              valuePropName="fileList"
              getValueFromEvent={normFile}
            >
              <Upload
                name="images"
                listType="picture-card"
                accept="image/jpeg,image/png,image/jpg,video/mp4,video/quicktime"
                multiple
                maxCount={5}
                beforeUpload={handleBeforeUpload}
                itemRender={(orgNode, file, _) => {
                  if (!file?.originFileObj) return orgNode;
                  const isVideo = file.type?.startsWith('video/');

                  let mediaUrl = fileUrlCache.current.get(file.uid);
                  if (!mediaUrl) {
                    mediaUrl = !isVideo ? file.thumbUrl : URL.createObjectURL(file.originFileObj);
                    fileUrlCache.current.set(file.uid, mediaUrl as string);
                  }

                  return (
                    <ImageContainer
                      key={mediaUrl}
                      onClick={() => {
                        setPreviewFile({
                          type: isVideo ? 'video' : 'image',
                          url: mediaUrl as string,
                        });
                      }}
                    >
                      {isVideo ? (
                        <>
                          <VideoFrame src={mediaUrl}/>
                          <PlayIconContainer>
                            <SVGIcon src={VideoPlayIcon} renderAsImg={false} />
                          </PlayIconContainer>
                        </>
                      ) : (
                        <ImageItem src={mediaUrl} alt="Review image" />
                      )}
                      <RemoveIconContainer
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFile(file);
                        }}
                      >
                        <SVGIcon src={RemoveIcon} renderAsImg={false} />
                      </RemoveIconContainer>
                      <OverlayImageContainer>
                        {isVideo ? (
                          <SVGIcon src={VideoPlayIcon} renderAsImg={false} />
                        ) : (
                          <EyeOutlined
                            style={{
                              fontSize: '20px',
                              color: '#ffffff',
                            }}
                          />
                        )}
                      </OverlayImageContainer>
                    </ImageContainer>
                  );
                }}
              >
                {(fileListData && fileListData.length >= 5) || !isShowAddImageBtn ? null : (
                  <PlusOutlined
                    style={{
                      fontSize: '18px',
                      color: '#43a047',
                    }}
                  />
                )}
              </Upload>
            </Form.Item>

            <AnonymousCommentContainer>
              <Form.Item name="isAnonymous" colon={false} required={false} valuePropName="checked">
                <Switch />
              </Form.Item>
              <AnonymousCommentLabel>{translations(utilMessages.anonymousComment)}</AnonymousCommentLabel>
            </AnonymousCommentContainer>
          </Form>
          <ConfirmButtonContainer>
            <ConfirmButton
              disabled={!commentValue || commentValue?.trim().length < 10}
              loading={isLoading}
              type="primary"
              onClick={() => {
                form.submit();
              }}
            >
              {translations(utilMessages.submitReview)}
            </ConfirmButton>
          </ConfirmButtonContainer>
        </Container>
      </AModal>

      {!!previewFile && (
        <Modal
          open={!!Object.keys(previewFile)}
          footer={null}
          onCancel={() => setPreviewFile(undefined)}
          width={512}
          centered
        >
          <div
            style={{
              width: '100%',
              height: 'auto',
              aspectRatio: '1/1',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {previewFile?.type === 'video' ? (
              <video
                autoPlay
                controls
                src={previewFile?.url}
                style={{
                  width: '100%',
                }}
              />
            ) : (
              <img src={previewFile?.url} />
            )}
          </div>
        </Modal>
      )}
    </>
  );
};

export default AddReviewProductModal;
